
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEmailVerificationAlert, setShowEmailVerificationAlert] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setShowEmailVerificationAlert(false);

    try {
      console.log('Attempting login with email:', email.trim());
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      if (error) {
        console.error('Login error:', error);
        let errorMessage = "An error occurred during login";
        
        if (error.message?.includes("Email not confirmed")) {
          setShowEmailVerificationAlert(true);
          return;
        } else if (error.message === "Invalid login credentials") {
          errorMessage = "Incorrect email or password. Please try again.";
        } else {
          errorMessage = error.message;
        }

        toast({
          title: "Login Failed",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      if (!data.user) {
        throw new Error("No user data returned");
      }

      console.log('Login successful for user:', data.user.id);
      
      toast({
        title: "Welcome back!",
        description: "Successfully logged in",
      });
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Unexpected login error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {showEmailVerificationAlert && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-700">
            <p className="mb-2">
              Please verify your email address before logging in. We sent a verification link to <span className="font-medium">{email}</span>.
            </p>
            <p>
              Check your inbox and spam folder. If you didn't receive the email, you can{" "}
              <Button 
                variant="link" 
                className="text-yellow-700 font-medium p-0 h-auto hover:text-yellow-800"
                onClick={async () => {
                  try {
                    const { error } = await supabase.auth.resend({
                      type: 'signup',
                      email: email,
                    });
                    
                    if (error) throw error;
                    
                    toast({
                      title: "Verification email sent",
                      description: "Please check your inbox for the verification link",
                    });
                  } catch (error) {
                    console.error("Error resending verification:", error);
                    toast({
                      title: "Error",
                      description: "Failed to resend verification email. Please try again.",
                      variant: "destructive",
                    });
                  }
                }}
              >
                click here to resend the verification email
              </Button>
              .
            </p>
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="password" className="text-sm font-medium">
          Password
        </label>
        <Input
          id="password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Please wait..." : "Sign In"}
      </Button>
    </form>
  );
};
